import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { LockFilled, LockOpen } from "@air/next-icons";
import { IconButton } from "@air/primitive-icon-button";
import { Tooltip } from "@air/primitive-tooltip";
import { tailwindMerge } from "@air/tailwind-variants";
import { useField } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DIMENSION_BAR_ASPECT_RATIO_BUTTON, DIMENSION_BAR_HEIGHT_INPUT, DIMENSION_BAR_WIDTH_INPUT } from "~/constants/testIDs";
import { DimensionBarInput } from "./components/DimensionBarInput";
import { calculateAspectRatio, calculateDimensionWithAspectRatio } from "./utils";
export var SizeValue;
(function(SizeValue) {
    SizeValue["Custom"] = "0";
    SizeValue["Original"] = "1";
})(SizeValue || (SizeValue = {}));
/**
 * @description This DimensionBar component is intended to be used in conjunction with a Formik form that contains an adjacent SingleSelect.
 * The form should include a `height` and a `width` field where each field is an integer/number. The Single Select should have at
 * least 2 values (Original and Custom).
 */ export const DimensionBar = (param)=>{
    let { dimensionUnits = "px", disabled = false, onError, onLinkClick, setIsEditing, className } = param;
    const [{ onChange: updateFormikWidth }, { error: widthError, initialValue: initialWidth = 0, value: width }, { setValue: setWidthValue }] = useField("width");
    const [{ onChange: updateFormikHeight }, { error: heightError, initialValue: initialHeight = 0, value: height }, { setValue: setHeightValue }] = useField("height");
    const [{ value: formAspectRatio }, _a, { setValue: setFormAspectRatio }] = useField("size");
    const aspectRatio = Number(formAspectRatio);
    const isSelectValueCustom = formAspectRatio === "0";
    const isSelectValueOriginal = formAspectRatio === "1";
    const isSelectValue = !(isSelectValueOriginal || isSelectValueCustom);
    const [isWidthFocus, setIsWidthFocus] = useState(false);
    const [isHeightFocus, setIsHeightFocus] = useState(false);
    const [isAspectRatioLocked, setIsAspectRatioLocked] = useState(!!aspectRatio);
    const [hasRecalculatedBothFields, setHasRecalculatedBothFields] = useState(false);
    const [customAspectRatio, setCustomAspectRatio] = useState();
    const originalAspectRatio = useMemo(()=>calculateAspectRatio(initialWidth, initialHeight), [
        initialHeight,
        initialWidth
    ]);
    const lockAspectRatio = ()=>setIsAspectRatioLocked(true);
    const unlockAspectRatio = ()=>setIsAspectRatioLocked(false);
    const updateCustomAspectRatio = useCallback(()=>{
        const cAspectRatio = calculateAspectRatio(width, height);
        setCustomAspectRatio(cAspectRatio);
    }, [
        height,
        width
    ]);
    const onClick = ()=>{
        isAspectRatioLocked ? unlockAspectRatio() : lockAspectRatio();
        const shouldSetCustomAspectRatio = formAspectRatio === "0" && !isAspectRatioLocked;
        if (shouldSetCustomAspectRatio) updateCustomAspectRatio();
        onLinkClick && onLinkClick(isAspectRatioLocked);
    };
    const resetValues = useCallback(()=>{
        setWidthValue(Number(initialWidth));
        setHeightValue(Number(initialHeight));
    // setHeightValue and setWidthValue are not stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        initialHeight,
        initialWidth
    ]);
    const updateWidthAndHeight = useCallback(()=>{
        if (!hasRecalculatedBothFields) {
            setWidthValue(calculateDimensionWithAspectRatio(initialWidth || 1, aspectRatio));
            setHeightValue(calculateDimensionWithAspectRatio(initialHeight || 1, aspectRatio));
            setHasRecalculatedBothFields(true);
        } else {
            setHasRecalculatedBothFields(false);
        }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        height,
        width,
        aspectRatio
    ]);
    const handleInputChange = (hasHeightChanged, value)=>{
        setFormAspectRatio("0");
        if (!isAspectRatioLocked) return;
        const aspectRatio = customAspectRatio ? customAspectRatio : originalAspectRatio;
        if (aspectRatio) {
            hasHeightChanged ? setWidthValue(calculateDimensionWithAspectRatio(value, 1 / aspectRatio)) : setHeightValue(calculateDimensionWithAspectRatio(value, aspectRatio));
        }
    };
    const onChange = (event)=>{
        const { name: inputName, value } = event.target;
        const hasHeightChanged = inputName === "height";
        const latestValue = Number(value);
        // // NOTE: We need to call the onChange provided from Formik in order to keep formik updated
        // // see: https://stackoverflow.com/a/48980424/8560463
        hasHeightChanged ? updateFormikHeight(event) : updateFormikWidth(event);
        handleInputChange(hasHeightChanged, latestValue);
    };
    useEffect(()=>{
        const allFieldsUnFocused = !isWidthFocus && !isHeightFocus;
        if (!allFieldsUnFocused) return;
        if (isSelectValue) updateWidthAndHeight();
        if (isSelectValueOriginal) resetValues();
    }, [
        isHeightFocus,
        isSelectValue,
        isSelectValueOriginal,
        isWidthFocus,
        resetValues,
        updateWidthAndHeight
    ]);
    const hasError = !!heightError || !!widthError;
    useEffect(()=>{
        if (onError) {
            onError(hasError);
        }
    }, [
        hasError,
        onError
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: tailwindMerge("flex max-h-[62px] flex-col justify-center text-grey-9", className),
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-4",
                onBlur: ()=>setIsEditing(false),
                onFocus: ()=>setIsEditing(true),
                children: [
                    /*#__PURE__*/ _jsx(DimensionBarInput, {
                        "data-testid": DIMENSION_BAR_WIDTH_INPUT,
                        dimensionMeasurement: "width",
                        dimensionUnits: dimensionUnits,
                        disabled: disabled,
                        onChange: onChange,
                        onFocus: setIsWidthFocus
                    }),
                    /*#__PURE__*/ _jsx(Tooltip, {
                        label: isAspectRatioLocked ? "Unlock aspect ratio" : "Lock aspect ratio",
                        side: "top",
                        children: /*#__PURE__*/ _jsx(IconButton, {
                            "data-testid": "".concat(DIMENSION_BAR_ASPECT_RATIO_BUTTON).concat(isAspectRatioLocked ? "LOCKED" : "UNLOCKED"),
                            disabled: disabled,
                            icon: isAspectRatioLocked ? LockFilled : LockOpen,
                            onClick: onClick,
                            appearance: "ghost",
                            color: "grey",
                            size: "small",
                            label: isAspectRatioLocked ? "Unlock aspect ratio" : "Lock aspect ratio"
                        })
                    }),
                    /*#__PURE__*/ _jsx(DimensionBarInput, {
                        "data-testid": DIMENSION_BAR_HEIGHT_INPUT,
                        dimensionMeasurement: "height",
                        dimensionUnits: dimensionUnits,
                        onChange: onChange,
                        disabled: disabled,
                        onFocus: setIsHeightFocus
                    })
                ]
            }),
            hasError && /*#__PURE__*/ _jsx("p", {
                "data-testid": "DIMENSION_BAR_ERROR",
                className: "mt-1 text-12 text-red-9",
                children: widthError || heightError
            })
        ]
    });
};
